import { AppIcon } from '@/components/ui/app-icon';
import { Button } from '@/components/ui/button';
import { UpgradeLink } from '@/components/upgrade/upgrade-link.component';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTheme } from '@/hooks/use-theme.hook';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { useOpenUpgrade } from '@/hooks/use-open-upgrade.hook';
import {
  currentPlanName,
  formatCredits,
  getCreditsUsage,
  getPlanCredits,
  type CreditsMetadata,
  type CreditStatus,
} from '@/lib/credits';
import { formatBillingDate } from '@/lib/billing-date';
import { selectSuggestedUpgradePlans } from '@/lib/plan-upgrade-utils';
import { toUpgradeTier } from '@/lib/upgrade-link';
import { cn } from '@/lib/utils';
import { pricingMoney } from '@/containers/settings/billing/pricing-v4';
import {
  useGetBillingProfileQuery,
  useGetPlansQuery,
} from '@/store/features/billing.feature';
import { useGetOrganizationCreditsQuery } from '@/store/features/extension.feature';
import { useGetOnboardingChecklistQuery } from '@/store/features/onboarding-checklist.feature';
import { Link } from '@tanstack/react-router';
import * as React from 'react';
import { useMemo, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import {
  iconArrowRight,
  iconChevronDown,
  iconCoin,
  iconCoins,
  iconEnvelope,
  iconGift,
  iconPhone,
  iconSpinnerThird,
} from '@/lib/icons';
import { brandPlanName } from '@/lib/brand';

type IconShim = React.ComponentType<{ className?: string }>;
const ArrowRight: IconShim = (p) => <AppIcon icon={iconArrowRight} {...p} />;
const Gift: IconShim = (p) => <AppIcon icon={iconGift} {...p} />;
const Loader2: IconShim = (p) => <AppIcon icon={iconSpinnerThird} {...p} />;
const Mail: IconShim = (p) => <AppIcon icon={iconEnvelope} {...p} />;
const Phone: IconShim = (p) => <AppIcon icon={iconPhone} {...p} />;

/** What a custom trigger shows: the balance, and how much of it is left. */
export type CreditsSummary = {
  /** The balance to the tenth, as the ledger holds it; null while unknown. */
  credits: number | null;
  isLoading: boolean;
  /** Share of the allowance left, 0 to 1; null when unknown. */
  percentage: number | null;
  status: CreditStatus;
};

type CreditsDropdownProps = {
  align?: 'start' | 'end';
  /** Which side of the trigger the menu opens on; below by default. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  triggerClassName?: string;
  /**
   * 'pill' (default): coin + balance button. 'sidebar': "9,175 credits ▾"
   * text trigger with a thin usage bar and an Add link beside it (Arc sidebar
   * footer). 'icon': coin only, with a tooltip (Arc collapsed rail).
   */
  variant?: 'pill' | 'sidebar' | 'icon';
  /**
   * Renders the trigger instead of the default button. The node must hold a
   * `DropdownMenuTrigger`; anything around it stays outside the menu.
   */
  renderTrigger?: (summary: CreditsSummary) => React.ReactNode;
};

export const CreditsDropdown = ({
  align = 'end',
  side,
  triggerClassName,
  variant = 'pill',
  renderTrigger,
}: CreditsDropdownProps) => {
  const posthog = usePostHog();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [open, setOpen] = useState(false);
  const [bonusDotSeen, setBonusDotSeen] = useState(
    () => localStorage.getItem('cl_bonus_dot_seen') === '1'
  );
  const { organizationId, trial, tierLabel } = useFeatureAccess();
  // The menu's upgrades open the guided wizard, plan picked.
  const openUpgrade = useOpenUpgrade();
  // Free Trial v1: a trial org keeps the Free plan attached underneath, so both
  // the plan label and the meter denominator would otherwise read Free/30 for
  // someone who was just handed a Scale trial. `tier_label` and
  // `trial.credits_granted` are the backend's display authority. Non-trial orgs
  // (trial === null, which is every org today) take neither branch.
  const trialActive = trial?.active === true;
  const trialCredits =
    trialActive &&
    typeof trial?.credits_granted === 'number' &&
    trial.credits_granted > 0
      ? trial.credits_granted
      : null;

  const { currentData: creditsData, isLoading: isLoadingCredits } =
    useGetOrganizationCreditsQuery(
      { organizationId },
      { skip: !organizationId }
    );

  const { currentData: availablePlans, isLoading: isLoadingPlans } =
    useGetPlansQuery({ organizationId }, { skip: !organizationId });
  const { data: checklistData } = useGetOnboardingChecklistQuery();
  const showBonusBadge =
    checklistData?.eligible_for_onboarding_bonus === true &&
    checklistData?.onboarding_bonus_claimed === false;

  const { currentData: billingInfo } = useGetBillingProfileQuery(
    { organizationId },
    { skip: !organizationId }
  );

  // A cancelled plan, or one staff set by hand, ends rather than resets.
  const endsOn = formatBillingDate(
    billingInfo?.ends_on ?? billingInfo?.staff_plan_ends_on
  );
  const renewalDate = formatBillingDate(billingInfo?.next_renewal_date);

  const getCurrentPlanName = () => {
    // Trial first — the Stripe/AppSumo labels below describe the plan the org
    // falls back to when the trial ends, not the one it is on right now.
    if (trialActive) return tierLabel;
    if (!availablePlans) return 'Loading...';
    // Three regimes, matching the BE stackable-credits handoff:
    //   - Stacked (AppSumo + paid Cleanlist plan, tier_key !== 'free'):
    //     composite label "<Cleanlist plan> + <AppSumo label>" so the
    //     user sees both subscriptions at a glance.
    //   - AppSumo-only (Stripe missing or on Free): AppSumo label.
    //   - Cleanlist-only / Free: Stripe current_plan name or "Free".
    const appsumo = availablePlans.appsumo;
    const currentPlan = availablePlans.current_plan;
    const isStacked = !!(
      appsumo &&
      currentPlan &&
      currentPlan.tier_key &&
      currentPlan.tier_key !== 'free'
    );
    if (isStacked) {
      return `${currentPlanName(availablePlans)} + ${appsumo!.label}`;
    }
    return appsumo?.label || currentPlanName(availablePlans) || 'Free';
  };

  // No balance read (loading, or the request failed) is unknown, not 0: a
  // network blip must not read as the org's credits gone.
  const calculateCredits = () =>
    creditsData ? Math.round(creditsData.count * 10) / 10 : null;
  // Credits running Clu work holds, across the org (a teammate's chat, a
  // Slack turn, an agent run). They are shown beside the balance, not taken
  // out of it, so the headline is what is available.
  const reservedCredits =
    typeof creditsData?.reserved === 'number' && creditsData.reserved > 0
      ? creditsData.reserved
      : 0;

  // ``getCreditsUsage`` handles the AppSumo + stacking denominator
  // math itself (see credits.ts). We just forward a sensible
  // ``name`` for the meter label — Stripe plan first when stacked,
  // since that's what KNOWN_TIER_TOTALS keys off; AppSumo alone for
  // AppSumo-only orgs; free fallback otherwise.
  const apiCreditsMetadata: CreditsMetadata | undefined = creditsData
    ? {
        count: creditsData.count,
        id: availablePlans?.current_plan?.id,
        name:
          availablePlans?.current_plan?.name ||
          availablePlans?.appsumo?.label ||
          'Free Tier',
      }
    : undefined;

  // During a trial the denominator is the granted trial allowance, not the
  // underlying plan's monthly refill. Same thresholds as ``getCreditsUsage`` so
  // the meter colours read identically either side of the trial.
  const trialCreditsUsage = (granted: number) => {
    // No credits payload (loading, or the read failed) is unknown, neither
    // empty nor full. A count of 0 painted a fresh trial red on every load,
    // and a full meter beside the dash said the whole grant was left when
    // the read had failed. The grant itself comes from the trial, so the
    // denominator stays.
    if (!creditsData) {
      return {
        availableCredits: 0,
        totalCredits: granted,
        percentage: null,
        status: 'unknown' as const,
      };
    }
    const availableCredits = creditsData.count;
    const percentage =
      granted > 0 ? Math.max(0, Math.min(1, availableCredits / granted)) : 0;
    const status: 'good' | 'warning' | 'critical' =
      percentage <= 0.2 ? 'critical' : percentage <= 0.5 ? 'warning' : 'good';
    return { availableCredits, totalCredits: granted, percentage, status };
  };

  const creditsUsage = trialCredits
    ? trialCreditsUsage(trialCredits)
    : getCreditsUsage(availablePlans, apiCreditsMetadata);

  const creditTone = {
    good: {
      icon: 'text-emerald-500',
      text: isDark ? 'text-emerald-300' : 'text-emerald-600',
    },
    warning: {
      icon: 'text-amber-500',
      text: isDark ? 'text-amber-300' : 'text-amber-600',
    },
    critical: {
      icon: 'text-rose-500',
      text: isDark ? 'text-rose-300' : 'text-rose-600',
    },
    unknown: {
      icon: 'text-amber-500',
      text: isDark ? 'text-amber-300' : 'text-amber-600',
    },
  }[creditsUsage.status];

  const availableUpgrades = useMemo(
    () =>
      selectSuggestedUpgradePlans({
        plans: availablePlans?.plans,
        currentPlanId: availablePlans?.current_plan?.id,
        onFallback: ({
          reason,
          availablePaidPlans,
          currentIndex,
          targetIndices,
        }) => {
          if (!import.meta.env.DEV) return;
          console.warn('Upgrade suggestion fallback applied', {
            reason,
            availablePaidPlans,
            currentIndex,
            targetIndices,
          });
        },
      }),
    [availablePlans]
  );

  const triggerClasses = cn(
    'flex h-8 items-center gap-1.5 px-2',
    'rounded-md transition-all duration-300',
    'bg-transparent',
    'border',
    isDark
      ? 'border-white/20 hover:bg-white/[0.08]'
      : 'border-black/15 hover:bg-black/4',
    'cursor-pointer',
    triggerClassName
  );

  const bonusDot = showBonusBadge && (
    <span className='absolute -top-1 -right-1 flex h-2.5 w-2.5'>
      {!bonusDotSeen && (
        <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75' />
      )}
      <span className='relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-500' />
    </span>
  );

  return (
    <>
      <DropdownMenu
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (nextOpen)
            posthog?.capture('credits_menu_opened', {
              variant,
              credits_status: creditsUsage.status,
            });
          if (nextOpen && showBonusBadge && !bonusDotSeen) {
            setBonusDotSeen(true);
            localStorage.setItem('cl_bonus_dot_seen', '1');
          }
        }}
      >
        {renderTrigger ? (
          renderTrigger({
            credits: calculateCredits(),
            isLoading: isLoadingCredits,
            percentage: creditsUsage.percentage,
            status: creditsUsage.status,
          })
        ) : variant === 'icon' ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <button
                  type='button'
                  aria-label={`${formatCredits(calculateCredits())} credits`}
                  className={cn(
                    'relative flex h-9 w-full items-center justify-center rounded-[10px] transition-colors hover:bg-background/60 focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:outline-hidden',
                    triggerClassName
                  )}
                >
                  {bonusDot}
                  <AppIcon
                    icon={iconCoins}
                    className={creditTone.icon}
                    style={{ fontSize: 16 }}
                  />
                </button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side='right' sideOffset={8}>
              {formatCredits(calculateCredits())} credits
            </TooltipContent>
          </Tooltip>
        ) : variant === 'sidebar' ? (
          <DropdownMenuTrigger asChild>
            <button
              type='button'
              className={cn(
                'relative flex shrink-0 items-center gap-1 rounded-md px-1 py-0.5 text-xs font-medium text-foreground transition-colors hover:bg-background/60 focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:outline-hidden',
                triggerClassName
              )}
            >
              {bonusDot}
              {isLoadingCredits ? (
                <Loader2 className='h-3 w-3 animate-spin' />
              ) : (
                `${formatCredits(calculateCredits())} credits`
              )}
              <AppIcon
                icon={iconChevronDown}
                className='text-muted-foreground'
                style={{ fontSize: 9 }}
              />
            </button>
          </DropdownMenuTrigger>
        ) : (
          <DropdownMenuTrigger asChild>
            <button className={cn(triggerClasses, 'relative')}>
              {bonusDot}
              <AppIcon
                icon={iconCoin}
                className={creditTone.icon}
                style={{ fontSize: 16 }}
              />
              <span
                className={cn('text-xs font-bold sm:text-sm', creditTone.text)}
              >
                {isLoadingCredits ? (
                  <Loader2 className='h-3 w-3 animate-spin' />
                ) : (
                  formatCredits(calculateCredits())
                )}
              </span>
            </button>
          </DropdownMenuTrigger>
        )}
        {variant === 'sidebar' && (
          <>
            <div
              role='meter'
              aria-label='Credits remaining'
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round((creditsUsage.percentage ?? 0) * 100)}
              // A balance not read yet, or a read that failed, is unknown:
              // say so rather than announce 0 left.
              aria-valuetext={
                creditsUsage.percentage === null ? 'Balance unknown' : undefined
              }
              className='h-1 min-w-0 flex-1 overflow-hidden rounded-full bg-foreground/10'
            >
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-500',
                  creditsUsage.status === 'critical'
                    ? 'bg-rose-500'
                    : creditsUsage.status === 'warning'
                      ? 'bg-amber-500'
                      : 'bg-primary'
                )}
                style={{
                  width: `${creditsUsage.percentage === null ? 0 : Math.max(2, creditsUsage.percentage * 100)}%`,
                }}
              />
            </div>
            <Link
              to='/plans'
              search={{
                credits_added: undefined,
                plan_name: undefined,
                status: undefined,
              }}
              className='shrink-0 rounded-md px-1 text-xs font-medium text-primary hover:underline focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:outline-hidden'
            >
              Add
            </Link>
          </>
        )}
        <DropdownMenuContent
          align={align}
          side={side}
          className={cn(
            'w-80 overflow-hidden rounded-xl border p-0',
            isDark ? 'border-white/10 bg-[#1a1a1a]' : 'border-gray-200 bg-white'
          )}
        >
          <div className='space-y-3 p-4'>
            <div>
              <div className='flex items-center justify-between'>
                <span className='text-sm font-semibold text-foreground'>
                  {isLoadingPlans && !trialActive
                    ? 'Loading...'
                    : getCurrentPlanName()}
                </span>
                <span className='text-xs text-muted-foreground'>
                  {creditsUsage.percentage !== null
                    ? `${Math.round(creditsUsage.percentage * 100)}% remaining`
                    : ''}
                </span>
              </div>

              <div className='mt-2 flex items-baseline gap-1.5'>
                <AppIcon
                  icon={iconCoin}
                  className='text-emerald-500'
                  style={{ fontSize: 16 }}
                />
                <span className='text-2xl font-bold text-foreground'>
                  {isLoadingCredits ? (
                    <Loader2 className='h-4 w-4 animate-spin' />
                  ) : (
                    formatCredits(calculateCredits())
                  )}
                </span>
                {/* `total &&` would render a 0 total as a bare "0". */}
                {(creditsUsage.totalCredits ?? 0) > 0 && (
                  <span className='text-sm text-muted-foreground'>
                    / {creditsUsage.totalCredits!.toLocaleString()}
                  </span>
                )}
              </div>

              {reservedCredits > 0 && (
                <p className='mt-1 text-xs text-muted-foreground'>
                  {formatCredits(reservedCredits)} held for Clu work in progress
                </p>
              )}

              <div
                className={cn(
                  'mt-2 h-1.5 w-full overflow-hidden rounded-full',
                  isDark ? 'bg-white/10' : 'bg-gray-100'
                )}
              >
                <div
                  className={cn(
                    'h-full rounded-full transition-all duration-500',
                    creditsUsage.status === 'critical'
                      ? 'bg-rose-500'
                      : creditsUsage.status === 'warning'
                        ? 'bg-amber-500'
                        : 'bg-emerald-500'
                  )}
                  style={{
                    width: `${creditsUsage.percentage === null ? 0 : Math.max(2, creditsUsage.percentage * 100)}%`,
                  }}
                />
              </div>

              {(endsOn || renewalDate) && (
                <p className='mt-1.5 text-xs text-muted-foreground'>
                  {endsOn ? `Plan ends ${endsOn}` : `Resets on ${renewalDate}`}
                </p>
              )}
            </div>

            <div
              className={cn(
                'flex items-center justify-center gap-3 rounded-lg px-3 py-2 text-xs text-muted-foreground',
                isDark ? 'bg-white/5' : 'bg-gray-50'
              )}
            >
              <span className='flex items-center gap-1'>
                <Mail className='h-2.5 w-2.5' /> 1 email ={' '}
                <AppIcon
                  icon={iconCoin}
                  className='text-emerald-500'
                  style={{ fontSize: 10 }}
                />{' '}
                1
              </span>
              <span
                className={cn(
                  'h-2.5 w-px',
                  isDark ? 'bg-white/10' : 'bg-gray-200'
                )}
              />
              <span className='flex items-center gap-1'>
                <Phone className='h-2.5 w-2.5' /> 1 phone ={' '}
                <AppIcon
                  icon={iconCoin}
                  className='text-emerald-500'
                  style={{ fontSize: 10 }}
                />{' '}
                10
              </span>
            </div>

            {!isLoadingPlans && availableUpgrades.length > 0 && (
              <div className='space-y-2'>
                <h4 className='text-[12px] font-medium text-muted-foreground'>
                  Upgrade
                </h4>
                {availableUpgrades.map((plan) => {
                  const monthlyPrice = plan.monthly_price?.price || 0;
                  const displayPrice = pricingMoney(monthlyPrice);
                  // New plans price and credit each seat, as the site
                  // quotes them; older plans carry one monthly total.
                  const perSeat = (plan.credits_per_seat ?? 0) > 0;
                  const planCredits = perSeat
                    ? plan.credits_per_seat!
                    : getPlanCredits(plan);

                  return (
                    <div
                      key={plan.id}
                      className={cn(
                        'rounded-xl border p-3',
                        isDark
                          ? 'border-white/10 bg-white/[0.03]'
                          : 'border-gray-200 bg-white'
                      )}
                    >
                      <div className='flex items-start justify-between'>
                        <div>
                          <h5 className='text-sm font-semibold text-foreground'>
                            {brandPlanName(plan.name)}
                          </h5>
                          {planCredits > 0 && (
                            <div className='mt-0.5 flex items-center gap-1'>
                              <AppIcon
                                icon={iconCoins}
                                className='text-emerald-500'
                                style={{ fontSize: 12 }}
                              />
                              <span className='text-xs font-medium text-emerald-600'>
                                {planCredits.toLocaleString()}
                                {perSeat ? ' credits / seat / mo' : '/mo'}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className='text-right'>
                          <span className='text-lg font-bold text-foreground'>
                            {displayPrice}
                          </span>
                          <span className='block text-[10px] text-muted-foreground'>
                            {perSeat ? 'per seat / month' : 'per month'}
                          </span>
                        </div>
                      </div>
                      <Button
                        size='sm'
                        className='mt-2.5 h-8 w-full text-xs'
                        onClick={() => {
                          setOpen(false);
                          openUpgrade({
                            plan: toUpgradeTier(plan.tier_key),
                            source: 'credits_menu',
                          });
                        }}
                      >
                        Upgrade to{' '}
                        {brandPlanName(
                          plan.name.split(' ').slice(0, -1).join(' ') ||
                            plan.name
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Onboarding bonus sticker */}
            {showBonusBadge && (
              <Link
                to='/plans'
                search={{
                  credits_added: undefined,
                  plan_name: undefined,
                  status: undefined,
                }}
                onClick={() => setOpen(false)}
                className={cn(
                  'flex items-center gap-2 rounded-lg px-3 py-2 transition-colors',
                  isDark
                    ? 'bg-amber-500/10 hover:bg-amber-500/15'
                    : 'bg-amber-50 hover:bg-amber-100/80'
                )}
              >
                <Gift className='h-4 w-4 shrink-0 text-amber-500' />
                <div className='min-w-0'>
                  <p className='text-xs font-semibold text-amber-600 dark:text-amber-400'>
                    20% credit boost available
                  </p>
                  <p className='text-[10px] text-amber-600/70 dark:text-amber-400/60'>
                    Upgrade to claim your onboarding bonus
                  </p>
                </div>
              </Link>
            )}

            <UpgradeLink
              source='credits_menu'
              onClick={() => setOpen(false)}
              className='block'
            >
              <Button
                variant='outline'
                className={cn(
                  'h-8 w-full text-xs',
                  isDark
                    ? 'border-white/10 hover:bg-white/5'
                    : 'border-gray-200 hover:bg-gray-50'
                )}
                size='sm'
              >
                Browse plans
                <ArrowRight className='ml-1.5 h-3 w-3' />
              </Button>
            </UpgradeLink>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
